'use client';
import { SDK } from '@absmartly/javascript-sdk';
import { datadogRum } from '@datadog/browser-rum';
import type { TabletOrMobileOrDesktop } from '@repo/utils/device-type/types';
import type { ABSmartlyContextData, SDKConfig } from '../types/absmartly';
import { defaultEventLogger } from './defaultEventLogger';
import { setupForcedVariants } from './setupForcedVariants';

export type HydrateContextParams = {
  sdkConfig: SDKConfig;
  contextData?: ABSmartlyContextData;
  anonymousId: string | null;
  deviceId: string;
  deviceType: TabletOrMobileOrDesktop;
  experimentPrefix?: string;
};

function handleError(error: unknown) {
  datadogRum.addError(error, { message: 'ABSmartly failed to hydrate on client' });
}

export const hydrateClientContext = ({
  sdkConfig,
  contextData,
  anonymousId,
  deviceId,
  deviceType,
  experimentPrefix = 'test_',
}: HydrateContextParams) => {
  if (!contextData || !anonymousId) {
    return null;
  }

  try {
    const sdk = new SDK({
      ...sdkConfig,
      retries: 10,
      timeout: 10000,
      eventLogger: sdkConfig.eventLogger ? sdkConfig.eventLogger : defaultEventLogger,
    });
    const clientContext = sdk.createContextWith(
      {
        units: {
          device_id: deviceId,
          anonymous_id: anonymousId,
        },
      },
      contextData,
    );

    // Because contextData is not a promise, the isReady can be used instead of
    // the ready function that returns a promise.
    if (!clientContext.isReady()) {
      handleError(new Error('ABSmartly context was not ready'));
      return null;
    }

    clientContext.attributes({
      url: window.location.href,
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      environment: sdkConfig.environment,
      device_type: deviceType,
    });
    setupForcedVariants(clientContext, experimentPrefix);
    datadogRum.addAction('ABContext hydrated', {
      anonymous_id: anonymousId,
      device_id: deviceId,
      device_type: deviceType,
      environment: sdkConfig.environment,
      page_url: window.location.href,
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
    return clientContext;
  } catch (error) {
    handleError(error);
    return null;
  }
};
